import {
  changeEmailErrorSchema,
  changeEmailFinalizeResponseSchema,
  changeEmailRequestResponseSchema,
  changePasswordErrorSchema,
  changePasswordResponseSchema,
  changePhoneErrorSchema,
  changePhoneFinalizeResponseSchema,
  changePhoneConfirmResponseSchema,
  changePhoneRequestResponseSchema,
  updateAvatarErrorSchema,
  updateAvatarResponseSchema,
  mapUpdateProfileFormToApiRequest,
  mapChangePasswordFormToApiRequest,
  mapChangeEmailFormToApiRequest,
  mapChangePhoneFormToApiRequest,
  mapUpdateAvatarFormToApiRequest,
  updateProfileErrorSchema,
  updateProfileResponseSchema,
  uploadAvatarErrorSchema,
  uploadAvatarResponseSchema,
} from './profile.schema'
import type {
  ChangeEmailConfirmInput,
  ChangeEmailFinalizeInput,
  ChangeEmailFinalizeResponse,
  ChangeEmailForm,
  ChangeEmailRequestResponse,
  ChangePasswordForm,
  ChangePasswordResponse,
  ChangePhoneConfirmInput,
  ChangePhoneConfirmResponse,
  ChangePhoneFinalizeInput,
  ChangePhoneFinalizeResponse,
  RegisterPhoneConfirmInput,
  RegisterPhoneFinalizeResponse,
  RegisterPhoneRequestResponse,
  ChangePhoneForm,
  ChangePhoneRequestResponse,
  ProfileResponse,
  UpdateAvatarForm,
  UpdateAvatarResponse,
  UpdateProfileForm,
  UpdateProfileResponse,
  UploadAvatarRequestInput,
  UploadAvatarResponse,
} from './profile.schema'
import type { ApiErrorResponse } from '@/api/auth/auth.types'
import { z } from 'zod'
import { apiClientJsonParsed } from '@/lib/api-client'
import { workspace } from '@imobdesk/contracts'

/**
 * Busca o perfil do usuário atual.
 * Valida a resposta no boundary com o contrato (`@imobdesk/contracts`).
 */
export async function getUserProfile(): Promise<ProfileResponse> {
  try {
    const response = await apiClientJsonParsed(
      '/api/v2/workspace/profile',
      workspace.profileResponseSchema,
      {
        method: 'GET',
      },
    )

    return response
  } catch (error: any) {
    // Se for erro da API, lança com a estrutura esperada
    if (error.data) {
      throw error.data as ApiErrorResponse
    }
    // Se for outro tipo de erro, lança genérico
    throw {
      message: 'Erro ao buscar perfil do usuário',
      statusCode: 500,
    } as ApiErrorResponse
  }
}

/**
 * Altera a senha do usuário
 */
export async function changePassword(
  data: ChangePasswordForm,
): Promise<ChangePasswordResponse> {
  try {
    const requestData = mapChangePasswordFormToApiRequest(data)

    const response = await apiClientJsonParsed(
      '/api/v2/workspace/profile/password',
      z.union([changePasswordResponseSchema, changePasswordErrorSchema]),
      {
        method: 'PUT',
        body: JSON.stringify(requestData),
      },
    )

    if (!response.success) {
      const errorData = changePasswordErrorSchema.parse(response)
      throw {
        message: errorData.error.message,
        statusCode: 400,
        code: errorData.error.code,
        details: errorData.error.details,
      }
    }

    return changePasswordResponseSchema.parse(response)
  } catch (error: any) {
    // Se for erro 401 (não autorizado)
    if (error?.status === 401 || error?.statusCode === 401) {
      throw {
        message: error?.data?.message || 'Unauthorized',
        statusCode: 401,
      }
    }

    // Se já é um erro tratado (400 com mensagem da API)
    if (error?.statusCode === 400 && error?.message) {
      throw error
    }

    // Se o erro já tiver o formato esperado, lança direto
    if (error?.statusCode) {
      throw error
    }

    // Para outros erros, lança erro genérico
    throw {
      message: 'Erro ao alterar senha',
      statusCode: error?.status || 500,
    }
  }
}

/**
 * Atualiza o perfil do usuário
 */
export async function updateProfile(
  data: UpdateProfileForm,
): Promise<UpdateProfileResponse> {
  try {
    const requestData = mapUpdateProfileFormToApiRequest(data)

    const response = await apiClientJsonParsed(
      '/api/v2/workspace/profile',
      z.union([updateProfileResponseSchema, updateProfileErrorSchema]),
      {
        method: 'PUT',
        body: JSON.stringify(requestData),
      },
    )

    if (!response.success) {
      const errorData = updateProfileErrorSchema.parse(response)
      throw {
        message: errorData.error.message,
        statusCode: errorData.error.code === 'CONFLICT' ? 409 : 400,
        code: errorData.error.code,
        details: errorData.error.details,
      }
    }

    return updateProfileResponseSchema.parse(response)
  } catch (error: any) {
    // Se for erro 401 (não autorizado)
    if (error?.status === 401 || error?.statusCode === 401) {
      throw {
        message: error?.data?.message || 'Unauthorized',
        statusCode: 401,
      }
    }

    // Se já é um erro tratado (400 ou 409 com mensagem da API)
    if (
      (error?.statusCode === 400 || error?.statusCode === 409) &&
      error?.message
    ) {
      throw error
    }

    // Se o erro já tiver o formato esperado, lança direto
    if (error?.statusCode) {
      throw error
    }

    // Para outros erros, lança erro genérico
    throw {
      message: 'Erro ao atualizar perfil',
      statusCode: error?.status || 500,
    }
  }
}

/**
 * Solicita mudança de email (envia PIN para email atual)
 */
export async function changeEmailRequest(
  data: ChangeEmailForm,
): Promise<ChangeEmailRequestResponse> {
  try {
    const requestData = mapChangeEmailFormToApiRequest(data)
    const response = await apiClientJsonParsed(
      '/api/v2/workspace/profile/change-email/request',
      z.union([changeEmailRequestResponseSchema, changeEmailErrorSchema]),
      {
        method: 'POST',
        body: JSON.stringify(requestData),
      },
    )

    if (!response.success) {
      const errorData = changeEmailErrorSchema.parse(response)
      throw {
        message: errorData.error.message,
        statusCode: errorData.error.code === 'CONFLICT' ? 409 : 400,
        code: errorData.error.code,
        details: errorData.error.details,
      }
    }

    return changeEmailRequestResponseSchema.parse(response)
  } catch (error: any) {
    if (error?.status === 401 || error?.statusCode === 401) {
      throw {
        message: error?.data?.message || 'Unauthorized',
        statusCode: 401,
      }
    }
    if (error?.statusCode) {
      throw error
    }
    throw {
      message: 'Erro ao solicitar mudança de email',
      statusCode: error?.status || 500,
    }
  }
}

/**
 * Confirma mudança de email com PIN do email atual (envia PIN para novo email)
 */
export async function changeEmailConfirm(
  data: ChangeEmailConfirmInput,
): Promise<ChangeEmailRequestResponse> {
  try {
    const response = await apiClientJsonParsed(
      '/api/v2/workspace/profile/change-email/confirm',
      z.union([changeEmailRequestResponseSchema, changeEmailErrorSchema]),
      {
        method: 'POST',
        body: JSON.stringify(data),
      },
    )

    if (!response.success) {
      const errorData = changeEmailErrorSchema.parse(response)
      throw {
        message: errorData.error.message,
        statusCode: 400,
        code: errorData.error.code,
      }
    }

    return changeEmailRequestResponseSchema.parse(response)
  } catch (error: any) {
    if (error?.status === 401 || error?.statusCode === 401) {
      throw {
        message: error?.data?.message || 'Unauthorized',
        statusCode: 401,
      }
    }
    if (error?.statusCode) {
      throw error
    }
    throw {
      message: 'Erro ao confirmar mudança de email',
      statusCode: error?.status || 500,
    }
  }
}

/**
 * Finaliza mudança de email com PIN do novo email
 */
export async function changeEmailFinalize(
  data: ChangeEmailFinalizeInput,
): Promise<ChangeEmailFinalizeResponse> {
  try {
    const response = await apiClientJsonParsed(
      '/api/v2/workspace/profile/change-email/finalize',
      z.union([changeEmailFinalizeResponseSchema, changeEmailErrorSchema]),
      {
        method: 'POST',
        body: JSON.stringify(data),
      },
    )

    if (!response.success) {
      const errorData = changeEmailErrorSchema.parse(response)
      throw {
        message: errorData.error.message,
        statusCode: errorData.error.code === 'CONFLICT' ? 409 : 400,
        code: errorData.error.code,
      }
    }

    return changeEmailFinalizeResponseSchema.parse(response)
  } catch (error: any) {
    if (error?.status === 401 || error?.statusCode === 401) {
      throw {
        message: error?.data?.message || 'Unauthorized',
        statusCode: 401,
      }
    }
    if (error?.statusCode) {
      throw error
    }
    throw {
      message: 'Erro ao finalizar mudança de email',
      statusCode: error?.status || 500,
    }
  }
}

/**
 * Solicita mudança de celular (envia PIN para celular atual)
 */
export async function changePhoneRequest(
  data: ChangePhoneForm,
): Promise<ChangePhoneRequestResponse> {
  try {
    const requestData = mapChangePhoneFormToApiRequest(data)
    const response = await apiClientJsonParsed(
      '/api/v2/workspace/profile/change-phone/request',
      z.union([changePhoneRequestResponseSchema, changePhoneErrorSchema]),
      {
        method: 'POST',
        body: JSON.stringify(requestData),
      },
    )

    if (!response.success) {
      const errorData = changePhoneErrorSchema.parse(response)
      throw {
        message: errorData.error.message,
        statusCode: errorData.error.code === 'CONFLICT' ? 409 : 400,
        code: errorData.error.code,
        details: errorData.error.details,
      }
    }

    return changePhoneRequestResponseSchema.parse(response)
  } catch (error: any) {
    if (error?.status === 401 || error?.statusCode === 401) {
      throw {
        message: error?.data?.message || 'Unauthorized',
        statusCode: 401,
      }
    }
    if (error?.statusCode) {
      throw error
    }
    throw {
      message: 'Erro ao solicitar mudança de celular',
      statusCode: error?.status || 500,
    }
  }
}

/**
 * Confirma mudança de celular com PIN do celular atual (envia PIN para novo celular)
 */
export async function changePhoneConfirm(
  data: ChangePhoneConfirmInput,
): Promise<ChangePhoneConfirmResponse> {
  try {
    const response = await apiClientJsonParsed(
      '/api/v2/workspace/profile/change-phone/confirm',
      z.union([changePhoneConfirmResponseSchema, changePhoneErrorSchema]),
      {
        method: 'POST',
        body: JSON.stringify(data),
      },
    )

    if (!response.success) {
      const errorData = changePhoneErrorSchema.parse(response)
      throw {
        message: errorData.error.message,
        statusCode: 400,
        code: errorData.error.code,
      }
    }

    return changePhoneConfirmResponseSchema.parse(response)
  } catch (error: any) {
    if (error?.status === 401 || error?.statusCode === 401) {
      throw {
        message: error?.data?.message || 'Unauthorized',
        statusCode: 401,
      }
    }
    if (error?.statusCode) {
      throw error
    }
    throw {
      message: 'Erro ao confirmar mudança de celular',
      statusCode: error?.status || 500,
    }
  }
}

/**
 * Finaliza mudança de celular com PIN do novo celular
 */
export async function changePhoneFinalize(
  data: ChangePhoneFinalizeInput,
): Promise<ChangePhoneFinalizeResponse> {
  try {
    const response = await apiClientJsonParsed(
      '/api/v2/workspace/profile/change-phone/finalize',
      z.union([changePhoneFinalizeResponseSchema, changePhoneErrorSchema]),
      {
        method: 'POST',
        body: JSON.stringify(data),
      },
    )

    if (!response.success) {
      const errorData = changePhoneErrorSchema.parse(response)
      throw {
        message: errorData.error.message,
        statusCode: errorData.error.code === 'CONFLICT' ? 409 : 400,
        code: errorData.error.code,
      }
    }

    return changePhoneFinalizeResponseSchema.parse(response)
  } catch (error: any) {
    if (error?.status === 401 || error?.statusCode === 401) {
      throw {
        message: error?.data?.message || 'Unauthorized',
        statusCode: 401,
      }
    }
    if (error?.statusCode) {
      throw error
    }
    throw {
      message: 'Erro ao finalizar mudança de celular',
      statusCode: error?.status || 500,
    }
  }
}

/**
 * Solicita cadastro do primeiro celular (alias de change-phone/request sem celular).
 */
export async function registerPhoneRequest(
  data: ChangePhoneForm,
): Promise<RegisterPhoneRequestResponse> {
  return changePhoneRequest(data)
}

/**
 * Confirma cadastro do primeiro celular (alias de change-phone/confirm no fluxo register).
 */
export async function registerPhoneConfirm(
  data: RegisterPhoneConfirmInput,
): Promise<RegisterPhoneFinalizeResponse> {
  const response = await changePhoneConfirm(data)
  if (!response.data) {
    throw {
      message: 'Resposta inválida ao confirmar celular',
      statusCode: 500,
    }
  }
  return {
    success: response.success,
    message: response.message,
    data: response.data,
  }
}

/**
 * Normaliza o tipo de arquivo para garantir compatibilidade
 */
function normalizeContentType(fileType: string): string {
  const normalized: { [key: string]: string } = {
    'image/jpeg': 'image/jpeg',
    'image/jpg': 'image/jpeg',
    'image/png': 'image/png',
    'image/webp': 'image/webp',
  }
  return normalized[fileType.toLowerCase()] || 'image/jpeg'
}

/**
 * Etapa 1: Solicita URL pré-assinada para upload de avatar
 * fileType é obrigatório - fileName é gerado automaticamente pelo backend
 * Avatar sempre será público
 */
export async function uploadAvatarRequest(
  fileType: string,
): Promise<UploadAvatarResponse> {
  try {
    // Normaliza o fileType antes de enviar
    const normalizedFileType = normalizeContentType(fileType)

    const requestData: UploadAvatarRequestInput = {
      fileType: normalizedFileType,
    }

    const response = await apiClientJsonParsed(
      '/api/v2/workspace/upload/avatar',
      z.union([uploadAvatarResponseSchema, uploadAvatarErrorSchema]),
      {
        method: 'POST',
        body: JSON.stringify(requestData),
      },
    )

    if (!response.success) {
      const errorData = uploadAvatarErrorSchema.parse(response)
      throw {
        message: errorData.error.message,
        statusCode: 400,
        code: errorData.error.code,
      }
    }

    return uploadAvatarResponseSchema.parse(response)
  } catch (error: any) {
    if (error?.status === 401 || error?.statusCode === 401) {
      throw {
        message: error?.data?.message || 'Unauthorized',
        statusCode: 401,
      }
    }
    if (error?.statusCode) {
      throw error
    }
    throw {
      message: 'Erro ao gerar URL de upload',
      statusCode: error?.status || 500,
    }
  }
}

/**
 * Etapa 2: Faz upload direto ao S3 usando a URL pré-assinada
 * Usa XMLHttpRequest para maior compatibilidade e confiabilidade
 *
 * IMPORTANTE: Adicionar Content-Type usando o fileType retornado pela API na etapa 1.
 * O backend sempre retorna 'image/jpeg' - usar exatamente esse valor.
 */
export async function uploadToS3(
  uploadUrl: string,
  file: File,
  fileType: string,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()

    xhr.open('PUT', uploadUrl)
    xhr.setRequestHeader('Content-Type', fileType) // Usar EXATAMENTE o fileType retornado na etapa 1

    xhr.onload = () => {
      if (xhr.status === 200) {
        resolve()
      } else {
        // Tentar ler resposta de erro para debug
        let errorMessage = `Erro no upload do arquivo ao S3: ${xhr.status} ${xhr.statusText}`
        try {
          if (xhr.responseText) {
            errorMessage += ` - ${xhr.responseText}`
          }
        } catch {
          // Ignorar se não conseguir ler resposta
        }
        reject({
          message: errorMessage,
          statusCode: xhr.status,
        })
      }
    }

    xhr.onerror = () => {
      reject({
        message: 'Erro de rede no upload ao S3',
        statusCode: 0,
      })
    }

    xhr.send(file)
  })
}

/**
 * Etapa 3: Atualiza avatar no perfil do usuário
 */
export async function updateAvatar(
  data: UpdateAvatarForm,
): Promise<UpdateAvatarResponse> {
  try {
    const requestData = mapUpdateAvatarFormToApiRequest(data)
    const response = await apiClientJsonParsed(
      '/api/v2/workspace/profile/avatar',
      z.union([updateAvatarResponseSchema, updateAvatarErrorSchema]),
      {
        method: 'PUT',
        body: JSON.stringify(requestData),
      },
    )

    if (!response.success) {
      const errorData = updateAvatarErrorSchema.parse(response)
      throw {
        message: errorData.error.message,
        statusCode: errorData.error.code === 'CONFLICT' ? 409 : 400,
        code: errorData.error.code,
        details: errorData.error.details,
      }
    }

    return updateAvatarResponseSchema.parse(response)
  } catch (error: any) {
    if (error?.status === 401 || error?.statusCode === 401) {
      throw {
        message: error?.data?.message || 'Unauthorized',
        statusCode: 401,
      }
    }
    if (error?.statusCode) {
      throw error
    }
    throw {
      message: 'Erro ao atualizar avatar',
      statusCode: error?.status || 500,
    }
  }
}
